import CryptoJS from 'crypto-js';

/**
 * Encryption utilities for HIPAA-compliant patient data
 * Decrypts data encrypted with AES on the backend
 */

// Get encryption key from environment variable
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

/**
 * Decrypt a single encrypted field
 * @param {string} encryptedText - The encrypted text from the server
 * @param {string} key - Encryption key (defaults to env variable)
 * @returns {string} Decrypted text or empty string if decryption fails
 */
export const decryptField = (encryptedText, key = ENCRYPTION_KEY) => {
  if (!encryptedText) return '';

  // If the field doesn't look encrypted, return as-is
  if (!encryptedText.includes('U2FsdGVk')) {
    return encryptedText;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(encryptedText, key);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    // If decryption results in empty string, there might be an issue
    if (!decrypted) {
      console.warn('Decryption resulted in empty string for field');
      return '[Decryption Failed]';
    }

    return decrypted;
  } catch (error) {
    console.error('Decryption error:', error);
    return '[Decryption Error]';
  }
};

/**
 * Decrypt all encrypted fields in a patient object
 * @param {Object} patient - Patient object with encrypted fields
 * @param {string} key - Encryption key (defaults to env variable)
 * @returns {Object} Patient object with decrypted fields
 */
export const decryptPatient = (patient, key = ENCRYPTION_KEY) => {
  if (!patient) return null;

  // If not marked as encrypted, return as-is
  if (!patient.isEncrypted) return patient;

  // List of fields to decrypt (matches backend encryption)
  const fieldsToDecrypt = [
    'name',
    'email',
    'phoneNumber',
    'age',
    'sex',
    'streetAddress',
    'extendedAddress',
    'locality',
    'region',
    'postalCode',
    'country',
    'emergencyName',
    'emergencyNumber',
    'hcp1Name',
    'hcp1Email',
    'hcp2Name',
    'hcp2Email',
    'treatments',
    'diagnosisDate',
    'typeOfHAE',
    'otherTreatments'
  ];

  // Create a new object with decrypted fields
  const decrypted = { ...patient };

  fieldsToDecrypt.forEach(field => {
    if (decrypted[field]) {
      decrypted[field] = decryptField(decrypted[field], key);
    }
  });

  // Mark as decrypted
  decrypted.isEncrypted = false;

  return decrypted;
};

/**
 * Decrypt an array of patient objects
 * @param {Array} patients - Array of patient objects
 * @param {string} key - Encryption key (defaults to env variable)
 * @returns {Array} Array of decrypted patient objects
 */
export const decryptPatientList = (patients, key = ENCRYPTION_KEY) => {
  if (!Array.isArray(patients)) return [];

  return patients.map(patient => decryptPatient(patient, key));
};

/**
 * Check if encryption key is configured
 * @returns {boolean} True if encryption key is set
 */
export const isEncryptionKeyConfigured = () => {
  return !!ENCRYPTION_KEY;
};

/**
 * Get a masked version of the encryption key for debugging
 * @returns {string} Masked key
 */
export const getMaskedEncryptionKey = () => {
  if (!ENCRYPTION_KEY) return 'NOT SET';

  const length = ENCRYPTION_KEY.length;
  if (length < 8) return '***';

  return `${ENCRYPTION_KEY.substring(0, 4)}...${ENCRYPTION_KEY.substring(length - 4)}`;
};
